import { Box, Pallet, PalletLayer, OptimizationResult, PalletDimensions } from '../types';

const DEFAULT_PALLET_DIMENSIONS: PalletDimensions = {
  length: 100, // cm
  width: 120, // cm
  maxHeight: 170 // cm
};

export class PalletOptimizer {
  private boxes: Box[] = [];
  private palletDimensions: PalletDimensions;

  constructor(palletDimensions?: PalletDimensions) {
    this.palletDimensions = palletDimensions || DEFAULT_PALLET_DIMENSIONS;
  }

  setPalletDimensions(dimensions: PalletDimensions): void {
    this.palletDimensions = dimensions;
  }

  addBox(box: Box): void {
    this.boxes.push(box);
  }

  calculateBoxesPerLayer(box: Box): number {
    // Calculate how many boxes fit in one layer
    const boxesPerRow = Math.floor(this.palletDimensions.length / box.length);
    const boxesPerColumn = Math.floor(this.palletDimensions.width / box.width);
    return boxesPerRow * boxesPerColumn;
  }

  calculateLayersPerPallet(box: Box): number {
    return Math.floor(this.palletDimensions.maxHeight / box.height);
  }

  optimize(): OptimizationResult {
    const pallets: Pallet[] = [];
    let currentPallet: Pallet | null = null;

    for (const box of this.boxes) {
      let remainingQuantity = box.quantity;

      while (remainingQuantity > 0) {
        if (!currentPallet || currentPallet.remainingHeight < box.height) {
          // Create new pallet
          currentPallet = {
            id: `pallet-${pallets.length + 1}`,
            layers: [],
            totalHeight: 0,
            remainingHeight: this.palletDimensions.maxHeight,
            asins: []
          };
          pallets.push(currentPallet);
        }

        // Calculate how many boxes fit in current layer
        const boxesPerLayer = this.calculateBoxesPerLayer(box);
        const layersPossible = Math.floor(currentPallet.remainingHeight / box.height);
        const boxesToAdd = Math.min(remainingQuantity, boxesPerLayer * layersPossible);

        if (boxesToAdd > 0) {
          // Add layer to current pallet
          const layersToAdd = Math.ceil(boxesToAdd / boxesPerLayer);
          for (let i = 0; i < layersToAdd; i++) {
            const boxesInThisLayer = Math.min(boxesPerLayer, boxesToAdd - (i * boxesPerLayer));
            const layer: PalletLayer = {
              boxes: Array(boxesInThisLayer).fill(box),
              height: box.height
            };
            currentPallet.layers.push(layer);
            currentPallet.totalHeight = Math.round((currentPallet.totalHeight + box.height) * 10) / 10;
            currentPallet.remainingHeight = Math.round((currentPallet.remainingHeight - box.height) * 10) / 10;
            
            // Track ASIN if present
            if (box.asin && !currentPallet.asins.includes(box.asin)) {
              currentPallet.asins.push(box.asin);
            }
          }
          remainingQuantity -= boxesToAdd;
        } else {
          // Can't fit any more boxes on this pallet
          currentPallet = null;
        }
      }
    }

    return {
      pallets,
      totalPallets: pallets.length,
      efficiency: this.calculateEfficiency(pallets),
      palletDimensions: this.palletDimensions
    };
  }

  private calculateEfficiency(pallets: Pallet[]): number {
    const totalVolume = pallets.reduce((sum, pallet) => 
      sum + (pallet.totalHeight * this.palletDimensions.length * this.palletDimensions.width), 0);
    const maxVolume = pallets.length * this.palletDimensions.maxHeight * this.palletDimensions.length * this.palletDimensions.width;
    return (totalVolume / maxVolume) * 100;
  }
} 